/**
 * Hook personnalisé pour la validation de l'email
 * Gère l'appel API pour vérifier si l'email existe déjà dans le système
 */
import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { useLazyCompteLoginExistQuery } from "../redux/slice/api/FO/compteApi";

interface UseEmailValidationParams {
  /** Valeur actuelle de l'email */
  email: string | undefined;
  /** ID de la demande d'inscription (pour exclure du contrôle) */
  idDemande?: number | null;
  /** ID du compte (pour exclure du contrôle) */
  idCompte?: number | null;
  /** Email initial (pour éviter de valider l'email actuel de la fiche) */
  initialEmail?: string | null;
  /** Afficher les toasts de notification (par défaut true) */
  showToasts?: boolean;
}

interface UseEmailValidationReturn {
  /** Message d'erreur de validation email (null si pas d'erreur) */
  emailApiError: string | null;
  /** Indique si une requête de validation est en cours */
  isLoading: boolean;
  /** Fonction pour vérifier manuellement l'email */
  checkEmail: (emailToCheck?: string) => Promise<boolean>;
  /** Réinitialiser l'erreur */
  clearError: () => void;
}

/**
 * Hook personnalisé pour gérer la validation de l'email
 * - Appelle l'API pour vérifier si l'email existe déjà
 * - Détecte les changements réels d'email
 * - Affiche les messages d'erreur appropriés
 * - Empêche la validation si l'email est inchangé
 */
export const useEmailValidation = ({
  email,
  idDemande = null,
  idCompte = null,
  initialEmail = null,
  showToasts = true
}: UseEmailValidationParams): UseEmailValidationReturn => {
  const [emailApiError, setEmailApiError] = useState<string | null>(null);
  const [checkEmailExist, { isLoading }] = useLazyCompteLoginExistQuery();

  /**
   * Vérifier manuellement si l'email existe déjà via l'API
   * @param emailToCheck - Email à vérifier (utilise email du watch si non fourni)
   * @returns true si l'email est disponible, false s'il existe déjà
   */
  const checkEmail = useCallback(async (emailToCheck?: string): Promise<boolean> => {
    const emailValue = emailToCheck || email;
    
    if (!emailValue || emailValue.trim().length === 0) {
      setEmailApiError(null);
      return true; // Email vide, on considère OK (la validation required le gérera)
    }

    // Vérifier que l'email est valide avant d'appeler l'API
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      // Email invalide, mais on ne bloque pas encore (la validation Yup le fera)
      setEmailApiError(null);
      return true;
    }

    // Ne pas valider si l'email est identique à l'email initial de la fiche
    if (initialEmail && emailValue.toLowerCase() === initialEmail.toLowerCase()) {
      setEmailApiError(null);
      return true;
    }

    try {
      const result = await checkEmailExist({
        Login: emailValue,
        IdDemande: idDemande,
        IdCompte: idCompte
      }).unwrap();
      
      // Si le compte existe
      if (result.Exist) {
        const errorMessage = "Cet email est déjà utilisé par un autre compte.";
        if (showToasts) {
          toast.error(errorMessage);
        }
        setEmailApiError(errorMessage);
        return false;
      }
      
      // Email disponible
      setEmailApiError(null);
      if (showToasts) {
        toast.success("Email disponible.");
      }
      return true;
    } catch (error: any) {
      console.error("Erreur lors de la vérification de l'email :", error);
      const errorMessage = error?.data?.Return_Message || "Erreur lors de la vérification de l'email.";
      setEmailApiError(errorMessage);
      if (showToasts) {
        toast.error(errorMessage);
      }
      return false;
    }
  }, [email, checkEmailExist, initialEmail, showToasts, idDemande, idCompte]);

  /**
   * Réinitialiser l'erreur d'email
   */
  const clearError = useCallback(() => {
    setEmailApiError(null);
  }, []);

  return {
    emailApiError,
    isLoading,
    checkEmail,
    clearError
  };
};
